/**
 * ================================================================
 * UI COMPONENT: NotificationTabs
 * ================================================================
 *
 * @component        NotificationTabs
 * @description      Tab navigation for notification status filtering
 * @category         UI Component
 *
 * @props
 * - activeTab: NotificationStatus - Currently active tab
 * - onTabChange: (tab: NotificationStatus) => void - Tab change handler
 * - unreadCount: number - Count of unread notifications
 * - className?: string - Additional CSS classes
 *
 * @styling
 * - Base: Tailwind CSS utility classes
 * - States: Active/inactive tab styling
 * - Responsive: Mobile-first approach
 *
 * @accessibility
 * - ARIA tabs pattern implementation
 * - Keyboard navigation support
 * - Screen reader friendly labels
 *
 * @usage
 * ```tsx
 * <NotificationTabs
 *   activeTab="All"
 *   onTabChange={handleTabChange}
 *   unreadCount={5}
 * />
 * ```
 * ================================================================
 */

import { memo } from 'react';
import { motion } from 'framer-motion';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/utils';
import type { NotificationStatus } from '@/types/notifications';

interface NotificationTabsProps {
  activeTab: NotificationStatus;
  className?: string;
  onTabChange: (tab: NotificationStatus) => void;
  unreadCount: number;
  type?: 'NotificationPanel' | 'NotificationDrawer';
}

interface TabConfig {
  count?: number;
  id: NotificationStatus;
  label: string;
  showBadge?: boolean;
}

export const NotificationTabs = memo(function NotificationTabs({
  activeTab,
  onTabChange,
  unreadCount,
  className,
  type = 'NotificationDrawer',
}: NotificationTabsProps) {
  // ================================================================
  // TAB CONFIGURATION
  // ================================================================
  //
  // Define all available tabs with their properties
  // Include badge display logic for unread count
  // ================================================================

  const tabs: TabConfig[] = [
    {
      id: 'All',
      label: 'All',
      showBadge: false,
    },
    {
      id: 'unread',
      label: 'Unread',
      showBadge: true,
      count: unreadCount,
    },
    {
      id: 'read',
      label: 'Read',
      showBadge: false,
    },
  ];

  /**
   * Handle tab click and change active tab
   *
   * @param {NotificationStatus} tabId - ID of clicked tab
   * @returns {void}
   */
  const handleTabClick = (tabId: NotificationStatus) => {
    if (tabId !== activeTab) {
      onTabChange(tabId);
    }
  };

  // Filter tabs based on type - hide "Read" tab for NotificationPanel
  const visibleTabs = tabs.filter((tab) => {
    if (type === 'NotificationPanel' && tab.id === 'read') {
      return false;
    }
    return true;
  });

  return (
    <div
      className={cn(
        // ================================================================
        // CONTAINER STYLES - Using design system tokens
        // ================================================================
        //
        // Layout: Horizontal tab container with proper spacing
        // Background: Uses muted background from design system
        // Borders: Clean borders with design system colors
        // ================================================================
        'flex w-full border-b border-border',
        'rounded-t-lg bg-muted/20',
        className,
      )}
      role="tablist"
      aria-label="Notification status filter tabs"
    >
      {visibleTabs.map((tab) => {
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`panel-${tab.id}`}
            id={`tab-${tab.id}`}
            onClick={() => handleTabClick(tab.id)}
            className={cn(
              // ================================================================
              // BASE TAB STYLES - Using design system
              // ================================================================
              //
              // Layout: Flex-1 for equal width distribution
              // Typography: Consistent font sizing from design system
              // Interaction: Smooth transitions and hover effects
              // ================================================================
              'relative flex flex-1 items-center justify-center gap-2 px-4 py-3',
              'text-sm font-medium transition-all duration-200',
              'hover:bg-accent/50',

              // ================================================================
              // ACTIVE/INACTIVE STATES - Using design system tokens
              // ================================================================
              //
              // Active: Primary color scheme with card background
              // Inactive: Muted colors with hover enhancement
              // ================================================================
              {
                'bg-card text-primary shadow-sm': isActive,
                'text-muted-foreground hover:text-foreground': !isActive,
              },
            )}
          >
            {/* ================================================================
                 TAB LABEL
                 ================================================================
                 
                 Tab text with proper typography
                 Maintains consistent spacing and alignment
                 ================================================================ */}
            <span>{tab.label}</span>

            {/* ================================================================
                 NOTIFICATION BADGE
                 ================================================================
                 
                 Badge for unread count on specific tabs
                 Only shown when enabled and count > 0
                 ================================================================ */}
            {tab.showBadge && tab.count && tab.count > 0 && (
              <Badge
                variant={isActive ? 'default' : 'secondary'}
                className={cn(
                  'flex h-5 w-5 items-center justify-center p-0 text-xs',
                  'transition-all duration-200',
                )}
              >
                {tab.count > 99 ? '99+' : tab.count}
              </Badge>
            )}

            {/* ================================================================
                 ACTIVE TAB INDICATOR
                 ================================================================
                 
                 Animated bottom border for active tab
                 Smooth transition with Framer Motion
                 ================================================================ */}
            {isActive && (
              <motion.div
                layoutId="activeTabIndicator"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 30,
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
});

/**
 * ================================================================
 * END OF COMPONENT: NotificationTabs
 * ================================================================
 *
 * @exports          NotificationTabs - Tab navigation component
 * @dependencies     React (memo), Framer Motion, Badge component
 * @usedBy           NotificationsPanel for status filtering
 *
 * @accessibility
 * - ARIA tablist pattern for screen readers
 * - Keyboard navigation with focus management
 * - Proper tab/tabpanel relationships
 *
 * ================================================================
 */
